import { useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, Reveal, SectionHead } from "@/components/site/Motion";
import { TripCard } from "@/components/site/TripCard";
import { PlacesExplorer } from "@/components/home/GeorgiaSpotlight";
import { CallbackSection } from "@/components/home/CallbackSection";
import { TrustBadges } from "@/components/site/TrustBadges";
import { PageGallery } from "@/components/destination/PageGallery";
import { ShareButtons } from "@/components/destination/ShareButtons";
import { NotFound } from "@/components/site/NotFound";
import { EImg, T, useContent } from "@/context/ContentContext";
import { useEnquiry } from "@/context/EnquiryContext";
import { useAuth } from "@/context/AuthContext";
import { usePages } from "@/hooks/usePages";
import { useSeo } from "@/hooks/useSeo";
import { useTrips } from "@/hooks/useTrips";
import { COUNTRY_PHOTOS, IMG } from "@/data/site";

const REASONS = [
  ["Handpicked routes", "Stays, drivers and sights we have checked ourselves."],
  ["Visa & planning help", "Our team guides you through the paperwork before you fly."],
  ["Group or private", "Join a group departure or travel on your own dates."],
  ["Support on the ground", "A team you can actually reach, before and during the trip."],
];

const heroDefault = (name) => (COUNTRY_PHOTOS[name] || IMG.groupSunset).replace(/w=160\b/, "w=1800");

function DestinationHero({ k, name, path }) {
  const ref = useRef(null);
  const { openEnquiry } = useEnquiry();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden bg-wine-deep text-cream" data-testid="destination-hero">
      <EImg place="top-28 right-5" as={motion.img} k={`${k}.hero.img`} style={{ y, scale }} src={heroDefault(name)} alt={name} className="absolute inset-0 h-full w-full object-cover opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-t from-wine-deep via-wine-deep/40 to-transparent pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-[100svh] flex flex-col justify-end pb-20 pt-32">
        <T k={`${k}.hero.eyebrow`} as="p" className="eyebrow text-gold mb-6">{`Trippy Tastic · ${name}`}</T>
        <h1 className="font-display font-extrabold tracking-[-0.05em] leading-[0.88] text-6xl sm:text-8xl lg:text-[9rem]" data-testid="destination-title">
          <MaskLines lines={[
            <T k={`${k}.hero.title.0`}>{`${name},`}</T>,
            <T k={`${k}.hero.title.1`} className="font-serif italic font-normal tracking-[-0.02em] text-gold">waiting for you.</T>,
          ]} />
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 max-w-3xl">
          <T k={`${k}.hero.intro`} as="p" className="text-cream/85 text-lg max-w-md">{`Group departures, honeymoons and private tours across ${name}, planned end to end by our team.`}</T>
          <button onClick={() => openEnquiry({ destination: name })} data-testid="destination-hero-enquire-btn"
            className="shrink-0 rounded-full bg-terra text-paper px-7 py-4 font-semibold hover:bg-[#a5370a] transition-colors">
            <T k={`${k}.hero.cta`}>{`Plan my ${name} trip`}</T>
          </button>
        </motion.div>
        <TrustBadges className="mt-8" />
        <ShareButtons k={k} name={name} path={path} className="mt-8" />
      </div>
    </section>
  );
}

function Reasons({ k, name }) {
  return (
    <section className="bg-paper py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead k={`${k}.why`} eyebrow={`Why ${name}`} title={["Your next trip,", "sorted."]} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 rounded-[28px] overflow-hidden">
          {REASONS.map(([t, d], i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-paper p-8 min-h-[220px] flex flex-col justify-between group hover:bg-cream transition-colors duration-500">
              <span className="font-serif italic text-5xl text-terra">0{i + 1}</span>
              <div>
                <T k={`${k}.why.${i}.title`} as="h3" className="font-display text-xl font-bold">{t}</T>
                <T k={`${k}.why.${i}.text`} as="p" className="mt-2 text-stone-600">{d}</T>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryHint({ show }) {
  const { editing } = useContent();
  if (!editing || !show) return null;
  return (
    <div className="bg-paper py-10 text-center" data-testid="gallery-empty-hint">
      <p className="inline-block rounded-full border border-dashed border-terra px-5 py-3 text-sm text-ink/80">
        Photo gallery is empty. Add photos in Admin &gt; Pages &gt; Gallery for this page.
      </p>
    </div>
  );
}

function DestinationBody({ page }) {
  const k = `dest.${page.slug}`;
  useSeo({
    title: `${page.name} Tour Packages from India`,
    description: `${page.name} tour packages from Delhi: group departures, honeymoons and private tours planned end to end by Trippy Tastic. Request a callback.`,
    path: page.path,
  });
  const name = page.name;
  const { trips } = useTrips();
  const mine = trips.filter((t) => (t.destination || "").toLowerCase() === page.name.toLowerCase());
  const places = Array.from({ length: 5 }, (_, i) => ({
    name: `Highlight ${i + 1}`, tag: "Must see", text: "Describe this place in a line or two.", image: heroDefault(page.name),
  }));
  return (
    <main data-testid={`destination-page-${page.slug}`}>
      <DestinationHero k={k} name={name} path={page.path} />
      <Reasons k={k} name={name} />
      <section className="bg-wine text-cream py-24 sm:py-32 grain relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead light k={`${k}.places`} eyebrow="Where we'll take you" title={["Places you'll", "fall for."]} />
          <PlacesExplorer places={places} k={`${k}.places`} />
        </div>
      </section>
      <PageGallery items={Array.isArray(page.gallery) ? page.gallery : []} k={k} name={name} />
      <GalleryHint show={!page.gallery?.length} />
      {mine.length > 0 && (
        <section className="bg-cream py-24 sm:py-32" data-testid="destination-trips-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionHead k={`${k}.trips`} eyebrow="Departures" title={[`${name} trips`, "you can book."]} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {mine.map((t, i) => <Reveal key={t.id} delay={(i % 3) * 0.08}><TripCard trip={t} /></Reveal>)}
            </div>
          </div>
        </section>
      )}
      <CallbackSection k={`${k}.callback`} preset={{ destination: page.name }} title={["Let's plan", `your ${name}.`]} />
    </main>
  );
}

export default function Destination() {
  const { slug } = useParams();
  const { bySlug, loaded } = usePages();
  const { user } = useAuth();
  const page = bySlug(slug);
  if (!loaded) return <main className="min-h-screen bg-wine-deep" data-testid="destination-loading" />;
  if (!page || page.kind !== "destination" || (page.hidden && user?.role !== "admin")) return <NotFound />;
  return <DestinationBody page={page} />;
}
